import React, { useState, useEffect, useMemo } from 'react';

import PropTypes from 'prop-types';
import { toastr } from 'react-redux-toastr';

import { toLocaleString, useDebounce } from '../../collums-components/helpers/index';

import View from '../common/View';
import Actions from '../common/TabContent/actions';
import MembershipFormModal from './MembershipFormModal';

import MembershipsApi from '../../api/MembershipsApi';
import TaxesApi from '../../api/TaxesApi';
import MembershipCategoriesApi from '../../api/MembershipCategoriesApi';

import addCopySufix from '../../services/addCopySufix';

import {
    IconButton,
    Link,
    TableContainer,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    Typography,
    withStyles,
    makeStyles,
    Checkbox,
    FormControlLabel
} from '@material-ui/core';
import { Clear, Search, AddCircle } from '@material-ui/icons';
import LoadingScreen from '../../collums-components/components/common/loadingScreen';
import { membershipsStyles } from './styles';
import { inputSearchStyles } from '../../collums-constants/styles/stylesheets/inputSearchStyles';
import { buttonsStyles } from '../../collums-constants/styles/stylesheets/buttonsStyles';
import ConfirmModal from '../common/ConfirmModal';
import Switch from '../../collums-components/components/common/customSwitch';
import { getLocationItem } from '../../collums-constants/utils';

const columns = [
    { field: 'Active' },
    { field: 'Name' },
    { field: 'Description', style: { width: '100' } },
    { field: 'Category' },
    { field: 'Net Price' },
    { field: 'Tax Type' },
    { field: 'Gross Price' },
    { field: 'Actions' }
];

const errorMessage = {
    title: 'Something went wrong',
    message: ''
};

function MembershipsView({ classes, clinic, clinics, isFromOrg, allClinics }) {
    const [isOpen, setIsOpen] = useState(false);
    const [memberships, setMemberships] = useState([]);
    const [value, setValue] = useState('');
    const [selectedMembership, setSelectedMembership] = useState({});
    const [count, setCount] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [isLoading, setIsLoading] = useState(false);
    const [confirmModal, setConfirmModal] = useState(false);
    const [modalTitle, setModalTitle] = useState('');
    const [modalContent, setModalContent] = useState('');
    const [membershipId, setMembershipId] = useState('');
    const [showArchived, setShowArchived] = useState(false);
    const showInactive = true;
    const [membershipTaxes, setMembershipTaxes] = useState([]);
    const [categories, setCategories] = useState([]);

    const filterDebounce = useDebounce(value, 600);

    const globalSearchStyles = makeStyles(inputSearchStyles)();
    const globalButtonsStyles = makeStyles(buttonsStyles)();

    const onArchiveConfirm = async () => {
        await handleArchive();
        await refreshList();
    };

    const openArchive = async id => {
        setMembershipId(id);
        const membership = memberships.find(element => element.id.toString() === id.toString()) || {};
        if (membership.archived) {
            await handleArchive(id);
            await refreshList();
            return true;
        }
        openConfirmModal(
            `${membership.archived ? 'Unarchived membership' : 'Archive membership'}`,
            `Are you sure you want to ${membership.archived ? 'unarchive' : 'archive'} this membership?`
        );
    };

    const getTaxes = async item => {
        try {
            const taxes = await TaxesApi.getTaxesForItem(item);
            setMembershipTaxes(taxes);
        } catch (err) {
            return toastr.error(err?.data?.message || 'Something went wrong');
        }
    };

    useEffect(() => {
        const run = async () => {
            await getTaxes('services');
        };
        run();
        // eslint-disable-next-line
    }, []);

    const closeConfirmModal = () => {
        setConfirmModal(false);
        setModalTitle('');
        setModalContent();
    };

    const openConfirmModal = (title, component) => {
        setConfirmModal(true);
        setModalTitle(title);
        setModalContent(component);
    };

    const refreshList = async () => {
        setIsLoading(true);
        try {
            const qs = queryParamsBuilder({
                value,
                pageSize: rowsPerPage,
                skip: page * rowsPerPage,
                archived: showArchived,
                inactive: showInactive,
                clinic: clinic,
                isFromOrg: isFromOrg || 'false'
            });
            const result = await MembershipsApi.listMemberships(qs);
            setMemberships(result.memberships);
            setCount(result.size);
        } catch {
            toastr.error(errorMessage.title, errorMessage.message);
        }
        setIsLoading(false);
    };

    const queryParamsBuilder = qs => {
        if (!qs) return '';
        const keys = Object.keys(qs);
        let string = '?';
        keys.filter(key => qs[key] || qs[key] === 0).forEach(key => {
            string = string.concat(`${key}=${qs[key]}&`);
            return string;
        });
        const queryString = encodeURI(string);
        return queryString;
    };

    const onChangePage = (e, newPage) => {
        setPage(newPage);
    };

    const onChangeRowsPerPage = e => {
        setPage(0);
        setRowsPerPage(parseInt(e.target.value));
    };

    const handleArchive = async id => {
        try {
            const archiveId = id || membershipId;
            const currentMembership = memberships.find(membership => membership.id.toString() === archiveId.toString());
            const archiveValue = isFromOrg
                ? !currentMembership.archived
                : !getLocationItem(currentMembership, clinic).archived;
            if (!archiveId) return;
            await MembershipsApi.archiveMembership(archiveId, archiveValue, isFromOrg, clinic);
            await refreshList();
        } catch (err) {
            toastr.error(err?.data?.message || errorMessage.title, errorMessage.message);
        }
    };

    const handleCopy = async id => {
        try {
            const membership = await MembershipsApi.getMembershipWithoutAppt(id);
            membership.name = addCopySufix(membership.name, memberships);

            delete membership.id;
            delete membership._id;
            delete membership.__v;
            delete membership.status;

            // Map populated refs back to IDs
            membership.category = membership.category?.id || membership.category;
            membership.tax = membership.tax?.id || membership.tax;
            if (membership.services) {
                membership.services = membership.services.map(svc => ({
                    ...svc,
                    service: svc.service?.id || svc.service
                }));
            }
            if (membership.locations) {
                membership.locations = membership.locations.map(loc => ({
                    ...loc,
                    clinic: loc.clinic?.id || loc.clinic,
                    tax: loc.tax?.id || loc.tax,
                    category: loc.category?.id || loc.category
                }));
            }

            await MembershipsApi.createMembership(membership);
            await refreshList();

            toastr.success('Membership successfully duplicated.');
        } catch (e) {
            toastr.error(e.data?.message || errorMessage.message);
        }
    };

    const getMembershipCategories = async nameInput => {
        try {
            const queryParams = `?archived=${false}&name=${
                nameInput?.length ? nameInput : ''
            }&inactive=${false}&count=${1000}&withSubcategories=${false}&clinic=${
                clinic ? clinic : ''
            }&isFromOrg=${isFromOrg}`;
            const result = await MembershipCategoriesApi.getCategories(queryParams);

            const sortedCategories = result.items.sort((a, b) => {
                const aName = a.name?.toLowerCase() || '';
                const bName = b.name?.toLowerCase() || '';
                return aName.localeCompare(bName);
            });

            setCategories(sortedCategories);
        } catch {
            toastr.error(errorMessage.title, errorMessage.message);
        }
    };

    const getParentCategoryName = categoryId => {
        return categories.find(el => el.id === categoryId)?.name || '';
    };

    const actions = {
        handleArchive: id => openArchive(id),
        handleDuplicate: id => handleCopy(id)
    };

    const getMembershipNetPrice = (membership, isTotal, salePrice) => {
        if (membership.netPrice) {
            return parseFloat(Number(membership.netPrice).toFixed(2));
        }
        let priceValue;
        const membershipTax =
            typeof membership.tax === 'object'
                ? membership.tax.rate
                : membershipTaxes.find(tax => tax.id === membership.tax)?.rate;
        const tax = membershipTax;
        priceValue = (salePrice || membership.salePrice) / (tax / 100 + 1);

        if (!isTotal) priceValue = priceValue / (membership.services?.length || 1);
        return parseFloat(Number(priceValue).toFixed(2));
    };

    const getMembershipGrossPrice = (membership, isTotal, salePrice) => {
        let priceValue = salePrice || membership.salePrice;
        if (!isTotal) priceValue = priceValue / (membership.services?.length || 1);
        return parseFloat(Number(priceValue).toFixed(2));
    };

    useEffect(() => {
        refreshList();
        getMembershipCategories();
        // eslint-disable-next-line
    }, [page, rowsPerPage, filterDebounce, showArchived]);

    const changeMembershipActive = async (membership, activeValue) => {
        if (!(membership || {}).id) return;
        if (membership.archived) {
            toastr.error(`Cannot change membership ${membership.name} active, because this membership is archived`);
            return;
        }
        try {
            await MembershipsApi.changeMembershipActive(membership.id, activeValue, isFromOrg, clinic);

            if (isFromOrg) {
                setMemberships([
                    ...memberships.map(m => {
                        if (m.id === membership.id) {
                            m.active = activeValue;
                        }
                        return m;
                    })
                ]);
            } else {
                setMemberships([
                    ...memberships.map(m => {
                        if (m.id === membership.id) {
                            m.locations = m.locations.map(l => {
                                if (l.clinic === clinic) {
                                    l.active = activeValue;
                                }
                                return l;
                            });
                        }
                        return m;
                    })
                ]);
            }

            toastr.success(`Membership ${membership.name} successfully updated`);
        } catch (err) {
            if (typeof err === 'object') {
                if (err.data && err.data.message) {
                    toastr.error(err.data.message);
                    return;
                }
            }
            toastr.error('Something went wrong');
        }
    };

    const getDescription = membership => {
        if (membership.description) {
            return membership.description;
        }
        if (!membership.services || membership.services.length === 0) return '';
        const descriptionArr = membership.services.map(s => {
            const serviceName = s.service?.name || 'Unknown';
            if (s.serviceType === 'repeating') {
                return `${s.quantity}x ${serviceName} every ${s.repeatEvery} months`;
            }
            return `${s.quantity}x ${serviceName}`;
        });
        return descriptionArr.join(' and ');
    };

    const renderRow = useMemo(() => {
        return memberships.map(membership => {
            return (
                <TableRow
                    key={membership.id}
                    style={{
                        backgroundColor: (() => {
                            if (isFromOrg ? !membership.archived : !getLocationItem(membership, clinic).archived) {
                                return 'transparent';
                            }
                            return '#ff9e9e';
                        })()
                    }}
                >
                    <TableCell>
                        <Switch
                            disabled={isFromOrg ? membership.archived : getLocationItem(membership, clinic).archived}
                            checked={isFromOrg ? membership.active : getLocationItem(membership, clinic).active}
                            onChange={(e, switchValue) => changeMembershipActive(membership, switchValue)}
                        />
                    </TableCell>
                    <TableCell>
                        <Typography>
                            <Link
                                onClick={() => {
                                    setSelectedMembership({ ...membership, prevName: membership.name });
                                    setIsOpen(true);
                                }}
                            >
                                {membership.name}
                            </Link>
                        </Typography>
                    </TableCell>
                    <TableCell>
                        <Typography>{getDescription(membership)}</Typography>
                    </TableCell>
                    <TableCell>
                        <Typography>
                            {membership.category?.parentCategory
                                ? getParentCategoryName(membership.category.parentCategory)
                                : membership.category?.name}
                        </Typography>
                    </TableCell>
                    <TableCell className={classes.cellRightAlign}>
                        <Typography>
                            {toLocaleString(
                                getMembershipNetPrice(
                                    isFromOrg ? membership : getLocationItem(membership, clinic, 'netPrice'),
                                    true
                                )
                            )}
                        </Typography>
                    </TableCell>
                    <TableCell>
                        <Typography>
                            {isFromOrg
                                ? membership?.itemTax?.name
                                : membershipTaxes.find(tax => tax.id === getLocationItem(membership, clinic, 'tax').tax)
                                      ?.name}
                        </Typography>
                    </TableCell>
                    <TableCell className={classes.cellRightAlign}>
                        <Typography>
                            {toLocaleString(
                                getMembershipGrossPrice(
                                    isFromOrg ? membership : getLocationItem(membership, clinic),
                                    true
                                )
                            )}
                        </Typography>
                    </TableCell>
                    <TableCell>
                        <Actions
                            isFromOrg={isFromOrg}
                            actions={actions}
                            id={membership.id}
                            isArchived={isFromOrg ? membership.archived : getLocationItem(membership, clinic).archived}
                        />
                    </TableCell>
                </TableRow>
            );
        });
        // eslint-disable-next-line
    }, [memberships]);

    return (
        <>
            <LoadingScreen isVisible={isLoading} />
            <View>
                <div className={classes.header}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <TextField
                            className={globalSearchStyles.inputSearch}
                            id="searchMembership"
                            variant="outlined"
                            value={value}
                            onKeyPress={e => {
                                if (e.key === 'Enter') {
                                    refreshList();
                                }
                            }}
                            onChange={e => {
                                setValue(e.target.value);
                                setPage(0);
                            }}
                            InputProps={{
                                endAdornment: (
                                    <>
                                        <IconButton
                                            className={globalSearchStyles.icons}
                                            onClick={() => {
                                                setValue('');
                                                setPage(0);
                                            }}
                                            disabled={!value}
                                        >
                                            <Clear style={!value ? { color: 'transparent' } : {}} />
                                        </IconButton>
                                        <IconButton
                                            position="end"
                                            onClick={() => refreshList()}
                                            className={globalSearchStyles.icons}
                                        >
                                            <Search />
                                        </IconButton>
                                    </>
                                )
                            }}
                        />
                        {(clinic === 'organisation' || allClinics.length === 1) && (
                            <div className={classes.rightActionsContainer}>
                                <IconButton
                                    onClick={() => {
                                        setSelectedMembership({});
                                        setIsOpen(true);
                                    }}
                                >
                                    <AddCircle className={globalButtonsStyles.addCircleStyle} />
                                </IconButton>
                            </div>
                        )}
                    </div>
                    <div className={classes.checkboxesContainer}>
                        <FormControlLabel
                            label="Show Archived"
                            control={
                                <Checkbox
                                    checked={showArchived}
                                    onChange={() => {
                                        setShowArchived(!showArchived);
                                    }}
                                />
                            }
                        />
                    </div>
                </div>
                <TableContainer className={classes.tableContainer}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                {columns.map(item => (
                                    <TableCell
                                        key={item.field}
                                        style={item.style}
                                        className={
                                            item.field === 'Net Price' || item.field === 'Gross Price'
                                                ? `${classes.cellRightAlign}`
                                                : ''
                                        }
                                    >
                                        {item.field}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>{memberships.length > 0 && renderRow}</TableBody>
                    </Table>
                </TableContainer>
                <div className={classes.fixedTableFooter}>
                    <TablePagination
                        width={'100%'}
                        count={count}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        rowsPerPageOptions={[10, 25, 50]}
                        onChangePage={onChangePage}
                        onChangeRowsPerPage={onChangeRowsPerPage}
                    />
                </div>
            </View>
            {isOpen && (
                <MembershipFormModal
                    getMembershipCategories={getMembershipCategories}
                    categories={categories}
                    setCategories={cats => setCategories(cats)}
                    open={isOpen}
                    title={selectedMembership.id ? `View/Edit ${selectedMembership.name} membership` : 'New membership'}
                    selectedMembership={selectedMembership}
                    onClose={() => setIsOpen(false)}
                    queryParamsBuilder={queryParamsBuilder}
                    refreshList={refreshList}
                    errorMessage={errorMessage}
                    getMembershipGrossPrice={getMembershipGrossPrice}
                    getMembershipNetPrice={getMembershipNetPrice}
                    clinic={clinic}
                    clinics={clinics}
                    isFromOrg={isFromOrg}
                    membershipTaxes={membershipTaxes}
                    allClinics={allClinics}
                />
            )}
            <ConfirmModal
                isOpen={confirmModal}
                setIsOpen={closeConfirmModal}
                onConfirm={onArchiveConfirm}
                title={modalTitle}
                content={modalContent}
            />
        </>
    );
}

MembershipsView.propTypes = {
    classes: PropTypes.object
};

export default withStyles(membershipsStyles)(MembershipsView);
